import { Injectable } from '@angular/core';

/** What the server hands back once someone has proved the email on a permit. */
export interface ViewGrantSource {
    token?: string;
    viewGrant?: string;
    viewGrantExpiresAt?: number;
    /** Covers every permit on the applicant's email address, not just this one. */
    accountGrant?: string;
}

interface StoredGrant {
    grant: string;
    expiresAt: number; // unix seconds, mirroring the server's own expiry
}

/**
 * Remembers, per permit, that this device has already cleared the email gate.
 *
 * We store the server's short-lived GRANT, never the applicant's email address: it
 * expires on its own, it's bound to the one permit it was issued for, and a shared
 * computer never ends up with someone's email sitting in local storage.
 *
 * Written both when the applicant confirms their email on the permit view and the
 * moment they submit an application - they just typed that address into the form we
 * validated, so re-asking on the very next click proves nothing.
 */
@Injectable({ providedIn: 'root' })
export class PermitViewGrantService {
    /** One applicant per device, so the account grant needs no discriminator. */
    private readonly accountKey = 'permit-account-grant';

    private key(token: string): string {
        return `permit-view-grant:${token}`;
    }

    /**
     * Store the grants carried on a submit/verify response. No-op for whichever
     * isn't present. The account grant is kept under a single key - there's one
     * applicant per device, and it covers all of their permits.
     */
    public remember(source?: ViewGrantSource | null): void {
        const token = source?.token;
        if (token && source?.viewGrant && source?.viewGrantExpiresAt) {
            this.write(this.key(token), { grant: source.viewGrant, expiresAt: source.viewGrantExpiresAt });
        }
        if (source?.accountGrant && source?.viewGrantExpiresAt) {
            this.rememberAccount(source.accountGrant, source.viewGrantExpiresAt);
        }
    }

    /** Store an account-wide grant (also used by the emailed "find my permits" link). */
    public rememberAccount(grant: string, expiresAt: number): void {
        this.write(this.accountKey, { grant, expiresAt });
    }

    /** The still-valid account-wide grant, or null. */
    public readAccount(): string | null {
        return this.readKey(this.accountKey);
    }

    public clearAccount(): void {
        try {
            localStorage.removeItem(this.accountKey);
        } catch {
            // Nothing to clean up if storage isn't available.
        }
    }

    /** The still-valid grant for this permit, or null. Expired entries are dropped. */
    public read(token: string): string | null {
        return this.readKey(this.key(token));
    }

    public clear(token: string): void {
        try {
            localStorage.removeItem(this.key(token));
        } catch {
            // Nothing to clean up if storage isn't available.
        }
    }

    private readKey(key: string): string | null {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return null;
            const stored = JSON.parse(raw) as StoredGrant;
            // Discard locally rather than making a call we already know will 403.
            if (!stored?.grant || !stored?.expiresAt || stored.expiresAt * 1000 <= Date.now()) {
                localStorage.removeItem(key);
                return null;
            }
            return stored.grant;
        } catch {
            try {
                localStorage.removeItem(key);
            } catch {
                // Storage unavailable - nothing to clean up.
            }
            return null;
        }
    }

    private write(key: string, stored: StoredGrant): void {
        try {
            localStorage.setItem(key, JSON.stringify(stored));
        } catch {
            // Storage unavailable (private mode / disabled) - just won't be remembered.
        }
    }
}
